// AppContent.tsx
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, Container, CssBaseline } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { useThemeMode } from "./components/ThemeContext";
import { createAppTheme } from "./theme/appTheme";

import NavigationBar from "./components/NavigationBar";
import { AppFooter } from "./components/AppFooter";
import HomePage from "./pages/HomePage";
import EventConfigurator from "./pages/EventConfigurator/EventConfigurator";
import Logout from "./components/Logout";
import RequirePermissions from "./components/RequirePermissions";
import { loginRequest } from "./authConfig";
import ImportPage from "./pages/ImportPage";
import AdminPage from "./pages/Admin";
import MaintenancePage from "./pages/MaintenancePage";
import { config } from "./config";
import About from "./pages/About";
import EventHistoryExplorer from "./pages/EventHistoryExplorer";
import { ExportCodePreferenceProvider } from "./context/ExportCodePreferenceContext";
import { UnsavedChangesProvider } from "./context/UnsavedChangesContext";
import MyWorkspace from "./pages/MyWorkspace";
import EventCodeRangesPage from "./pages/EventCodeRangesPage";

const AppContent: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [authChecked, setAuthChecked] = useState(false);
  const isAuthenticated = accounts.length > 0;

  const { mode } = useThemeMode();
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    console.log("Redirect URI:", config.redirectUri);
    const checkAuth = async () => {
      try {
        const response = await instance.handleRedirectPromise();

        if (response) {
          instance.setActiveAccount(response.account);
        }

        setAuthChecked(true);
      } catch (error) {
        console.error("Fehler beim Redirect-Handling:", error);
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (authChecked && !isAuthenticated) {
      instance.loginRedirect(loginRequest);
    }
  }, [authChecked, isAuthenticated, instance]);

  if (!authChecked || (!isAuthenticated && authChecked)) {
    return null;
  }


  if (config.underMaintenance === true && isAuthenticated) {
    return (
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenancePage />
      </MuiThemeProvider>
    );
  }


  return (
    <MuiThemeProvider theme={theme}>
      {/* Inside the theme provider on purpose: CssBaseline paints <body> from the theme it
          sees, and <body> is what shows through wherever the shell below does not paint — for
          instance past the end of a page taller than the shell's own 100vh. */}
      <CssBaseline />
      <ExportCodePreferenceProvider>
        <UnsavedChangesProvider>
          <Router>
            {/* The document scrolls, not an inner box. An inner scroll container keeps the
                scrollbar off the document, so the header spanned the full window width while
                EngineeringTools and VehicleDebugger span window-minus-scrollbar — the header
                then sat ~7px further right than in the other two tools. `width: 100%` rather
                than `100vw` for the same reason: 100vw includes the scrollbar. */}
            <Box
              display="flex"
              flexDirection="column"
              minHeight="100vh"
              width="100%"
              sx={{ backgroundColor: theme.palette.background.default }}
            >
              <NavigationBar />

          <Box
            component="main"
            sx={{
              flex: 1,
              // Scroll anchoring is disabled on <body> in index.css now that the document is
              // the scroll container — see the note there.
              px: { xs: 1, sm: 2, md: 3 },
              py: { xs: 2, sm: 3 },
            }}
          >
            <Container maxWidth="xl">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/event-code-ranges" element={<EventCodeRangesPage />} />
                <Route path="/event-configurator" element={
                  <RequirePermissions creatorNeeded={true} adminNeeded={false}>
                    <EventConfigurator />
                  </RequirePermissions>} />
                <Route path="/maintenance" element={<MaintenancePage />} />
                <Route path="/import"
                  element={
                    <RequirePermissions creatorNeeded={true} adminNeeded={true}>
                      <ImportPage />
                    </RequirePermissions>
                  }
                />
                <Route path="/admin"
                  element={
                    <RequirePermissions creatorNeeded={false} adminNeeded={true}>
                      <AdminPage />
                    </RequirePermissions>
                  }
                />
                <Route path="/about" element={<About />} />
                <Route path="/event-history" element={<EventHistoryExplorer />} />
                <Route
                  path="/my-workspace"
                  element={
                    <RequirePermissions creatorNeeded={true} adminNeeded={false}>
                      <MyWorkspace />
                    </RequirePermissions>
                  }
                />
                <Route path="/logout" element={<Logout />} />
              </Routes>
            </Container>
          </Box>

          <AppFooter
            brand="NEURA Mobile Robots · internal event management"
            version={process.env.REACT_APP_VERSION}
            branch={process.env.REACT_APP_GIT_BRANCH}
            buildDate={process.env.REACT_APP_BUILD_DATE}
          />
            </Box>
          </Router>
        </UnsavedChangesProvider>
      </ExportCodePreferenceProvider>
    </MuiThemeProvider>
  );
};

export default AppContent;